import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useAppStore } from '@/stores/appStore';
import { useAuth } from '@/auth/useAuth';
import { fetchTriggerCallbackUrl } from '@/services/monitoringApi';

/**
 * Fetches the runtime callback URL for an HTTP Request trigger via the
 * data-plane `listCallbackUrl` endpoint and caches the result in React Query.
 *
 * The Logic Apps designer historically called this on the ARM control plane;
 * Otto routes the same call through `monitoringApi.fetchTriggerCallbackUrl`,
 * which targets the workflow runtime data-plane.
 *
 * The returned `value` is the URL displayed in the read-only `callbackUrl`
 * parameter (manifest declares `'x-ms-editor': 'copyable'`). The field is
 * marked `'x-ms-serialization': { skip: true }`, so populating it never
 * mutates the workflow definition.
 *
 * @param triggerName - The workflow trigger name (e.g. `When_a_HTTP_request_is_received`)
 * @param enabled - Caller-controlled gate (e.g. only true for Request triggers)
 */
export const useTriggerCallbackUrl = (
  triggerName: string | undefined,
  enabled: boolean,
) => {
  const { workflowName } = useParams<{ workflowName: string }>();
  const selectedApp = useAppStore((s) => s.selectedApp);
  const selectedProject = useAppStore((s) => s.selectedProject);
  const { getOttoToken } = useAuth();

  const isReady =
    enabled &&
    !!triggerName &&
    !!workflowName &&
    !!selectedApp &&
    !!selectedProject;

  const queryFn = useCallback(async () => {
    const accessToken = await getOttoToken();
    if (!accessToken) {
      throw new Error('Failed to acquire access token');
    }
    return fetchTriggerCallbackUrl(
      {
        projectName: selectedProject ?? '',
        appName: selectedApp?.name ?? '',
        workflowName: workflowName ?? '',
        accessToken,
      },
      triggerName ?? '',
    );
  }, [getOttoToken, selectedProject, selectedApp?.name, workflowName, triggerName]);

  return useQuery({
    queryKey: [
      'triggerCallbackUrl',
      selectedApp?.name,
      selectedProject,
      workflowName,
      triggerName,
    ],
    queryFn,
    enabled: isReady,
    staleTime: (query) =>
      query.state.data?.value ? 5 * 60 * 1000 : 0,
    refetchOnMount: true,
    retry: 1,
  });
};
